import { AlertCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useGetBlobDetailQuery } from '@/services/api/blobService';
import { useGetMatterQuery } from '@/services/api/mattersService';
import { Blob } from '@/services/types/blob-types';
import { CollectionNode } from '@/services/types/collection-types';

import { useDocumentTree } from '../../context/DocumentTreeContext';
import { AuditFlagOverlay } from './AuditFlagOverlay';
import { AuditWarningBanner } from './AuditWarningBanner';
import { BlobPreview } from './BlobPreview';
import { InspectorSkeleton } from './components/InspectorSkeleton';
import { EmptyState } from './EmptyState';
import { FolderInspector } from './FolderInspector';

export interface DocumentInspectorProps {
  blobs?: Blob[];
  matterId?: string | null | undefined;
}

// This component handles fetching and displaying blob details
const BlobDetailView = ({
  matterId,
  blob,
}: {
  matterId: string | null | undefined;
  blob: Blob;
}) => {
  const [viewAnyway, setViewAnyway] = useState(false);

  // Reset viewAnyway when blob changes
  useEffect(() => {
    setViewAnyway(false);
  }, [blob.id]);

  // Fetch matter data to get matter name for pinning functionality
  const { data: matter } = useGetMatterQuery(matterId || '', {
    skip: !matterId,
  });

  // Skip query if matterId is not available
  const {
    data: blobDetail,
    isLoading,
    error,
    refetch,
  } = useGetBlobDetailQuery(
    { matterId: matterId || '', blobId: blob?.id || '' },
    { skip: !matterId || !blob?.id },
  );

  // Validate blob input
  if (!blob || !blob.id) {
    return (
      <Alert variant="destructive" className="m-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          Invalid file data provided. Cannot display details.
        </AlertDescription>
      </Alert>
    );
  }

  // Loading state
  if (isLoading) {
    return <InspectorSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive" className="m-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          Failed to load document details. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  const isFlagged =
    blob.artifact_audit?.status === 'FLAGGED_PENDING_REVIEW';
  const artifactId = blob.artifact_id;

  // Show flag overlay when flagged and user hasn't clicked "view anyway"
  if (isFlagged && !viewAnyway && blob.artifact_audit && artifactId && matterId) {
    return (
      <AuditFlagOverlay
        audit={blob.artifact_audit}
        artifactId={artifactId}
        matterId={matterId}
        onViewAnyway={() => setViewAnyway(true)}
      />
    );
  }

  // Success state - render preview (with optional warning banner)
  return (
    <div className="h-full flex flex-col p-2">
      {isFlagged && viewAnyway && blob.artifact_audit && artifactId && matterId && (
        <AuditWarningBanner
          audit={blob.artifact_audit}
          artifactId={artifactId}
          matterId={matterId}
        />
      )}
      <div className="flex-1 min-h-0">
        <BlobPreview
          file={blob}
          matterId={matterId || undefined}
          matterName={matter?.name}
        />
      </div>
    </div>
  );
};

export const DocumentInspector = ({
  blobs,
  matterId,
  matterName,
}: DocumentInspectorProps & { matterName?: string }) => {
  const { selectedFileIds, activeNodeId, nodeMaps } = useDocumentTree();

  // Compute selected blob/folder directly during render (no useEffect timing issues)
  const selectedFolder = useMemo(() => {
    if (!activeNodeId) return null;
    if (nodeMaps.idToNodeMap.has(activeNodeId)) {
      return nodeMaps.idToNodeMap.get(activeNodeId) as CollectionNode;
    }
    return null;
  }, [activeNodeId, nodeMaps]);

  const selectedBlob = useMemo(() => {
    if (!activeNodeId) return null;
    // Only look for blob if it's not a folder
    if (nodeMaps.idToNodeMap.has(activeNodeId)) return null;
    if (!blobs) return null;
    return blobs.find((b) => b.id === activeNodeId) || null;
  }, [activeNodeId, blobs, nodeMaps]);

  // If no active node
  if (!activeNodeId) {
    return <EmptyState variant="no-selection" />;
  }

  // If multiple files are selected (checkbox selection)
  if (selectedFileIds.size > 1) {
    // Get the selected blobs for Pin All functionality
    const selectedBlobs = blobs?.filter((blob) => selectedFileIds.has(blob.id));
    return (
      <EmptyState
        variant="multiple"
        count={selectedFileIds.size}
        selectedBlobs={selectedBlobs}
        matterId={matterId || undefined}
        matterName={matterName}
      />
    );
  }

  // If folder is selected
  if (selectedFolder) {
    // Check if it's the root node
    const isRootNode =
      selectedFolder.parent_id === null || selectedFolder.depth === 0;

    if (isRootNode) {
      return <EmptyState variant="no-selection" />;
    }

    return <FolderInspector folder={selectedFolder} />;
  }

  // If single file is selected but file not found
  if (!selectedBlob) {
    return <EmptyState variant="not-found" />;
  }

  // If we're here, we have a selected blob and can fetch its details
  return <BlobDetailView matterId={matterId} blob={selectedBlob} />;
};
